import { z } from 'zod';

// Quotations, documents and messages as the app shows them. The server builds these from the Serb Mind vault
// (Data/quotations.csv, documents.csv, messages.csv).

export const QUOTE_STATUSES = ['Draft', 'Sent', 'Accepted', 'Expired', 'Rejected'] as const;
export type QuoteStatus = (typeof QUOTE_STATUSES)[number];
export type MoneyCurrency = 'USD' | 'EUR' | 'JOD';

/** One way to ship it: what it costs us and the price we would quote */
export type PriceOption = { service: string; transit: string; cost: number; price: number };

export type Quotation = {
  srb: string;
  /** the customer it was sent to */
  client: string;
  /** the number printed on the quotation, e.g. QUOTE-26-1305-4 */
  number: string;
  status: QuoteStatus;
  currency: MoneyCurrency;
  price: number;
  /** "25 Jul 2025", or "–" */
  validUntil: string;
  created: string;
  updated: string;
  /** airlines or shipping lines */
  carriers: string;
  options: PriceOption[];
  notes: string;
  /** the Drive path of the PDF, "" when there is none */
  pdf: string;
};

export const DOC_STATUSES = ['Received', 'Missing', 'Needs Review', 'Verified', 'Draft'] as const;
export type DocStatus = (typeof DOC_STATUSES)[number];

/** The document REGISTER: a record of each document, not the file */
export type DocRecord = {
  id: string;
  /** the name without the extension, as shown in the list */
  name: string;
  /** "pdf", "xlsx", "docx", or "" */
  ext: string;
  /** the shipment, or "" while the document is not assigned to one */
  srb: string;
  type: string;
  source: string;
  status: DocStatus;
  /** "2025-07-23" */
  date: string;
  /** where the file is on Drive; empty when there is no file yet */
  path: string;
  notes: string;
};

/** A date the vault says is coming up: a next step, a sailing or flight, a quotation running out, an invoice falling due */
export type VaultDate = {
  id: string;
  /** "2026-10-20" */
  date: string;
  kind: 'Next step' | 'ETD' | 'ETA' | 'Quotation valid until' | 'Invoice due';
  title: string;
  /** the shipment it belongs to, "" when none */
  code: string;
  /** who or what it concerns (the customer, the agent, the invoice party) */
  who: string;
  /** more detail, e.g. what the next step is */
  note: string;
  /** Ibraheem marked it done in the app (Claude then updates the vault) */
  done: boolean;
};

/** Marks one date from the vault as done; the id and date must be those of a date that is listed */
export const VaultDateDoneSchema = z.object({ id: z.string().min(1), date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/) });
export type VaultDateDone = z.infer<typeof VaultDateDoneSchema>;

export const PARTY_KEYS = ['Customer', 'Overseas Agent', 'Accountant'] as const;
export type PartyKey = (typeof PARTY_KEYS)[number];

/** One email or WhatsApp event: the messages themselves stay in the mail app, the vault keeps this one-line record */
export type MessageRecord = {
  id: string;
  /** "2026-10-18" */
  date: string;
  srb: string;
  party: PartyKey;
  /** the company or person on the other side */
  org: string;
  /** 'in' = they wrote to us, 'out' = we wrote to them */
  direction: 'in' | 'out';
  channel: string;
  subject: string;
  summary: string;
  needsReply: boolean;
};
